import { RouterChildContext } from 'react-router-dom';

import {
  AvailableFeatures,
  ManagerAccountMe,
} from '@/domain/models/AuthEnterprise';
import { MOS_ENTERPRISE_IDS, PROXY_LOGIN_ENTERPRISE_IDS } from '@/global/env';

type Type = (
  availableFeature: AvailableFeatures,
  history: RouterChildContext['router']['history'],
  me?: ManagerAccountMe | undefined
) => void;

export const redirect404Page: Type = (availableFeature, history, me) => {
  if (me === undefined) return;
  if (me.available_features.includes(availableFeature)) return;
  history.push('/404');
};

export const isAvailableFeature: (
  availableFeature: AvailableFeatures,
  me?: ManagerAccountMe
) => boolean = (availableFeature, me) => {
  if (me === undefined) return false;
  return me.available_features.includes(availableFeature);
};

/**
 * POS連携再送信 (SCR001/SCR002/SCR003) を開けるかどうかを判定します。
 *
 * 管理者と店舗責任者の両方を通します。店舗責任者へ見せる注文の範囲は
 * バックエンドが role_type から解決して絞るため、画面へ入れるかどうかは
 * 企業IDの許可リストだけで決めます。
 */
export const canUseMosPosResendPage = (
  me?: ManagerAccountMe,
  enterpriseIds: number[] = MOS_ENTERPRISE_IDS
): boolean => me !== undefined && enterpriseIds.includes(me.enterprise_id);

export const redirect404MosPosResendPage = (
  history: RouterChildContext['router']['history'],
  me?: ManagerAccountMe
): void => {
  if (me === undefined || canUseMosPosResendPage(me)) return;
  history.push('/404');
};

/**
 * POSメニューマスタ (SCR009/SCR010) を操作できるかどうかを判定します。
 *
 * available_features ではなく企業IDの許可リストで判定します。この画面は
 * auth_enterprises_available_dashboard_features にレコードを必要としません。
 * なお最終的な可否はバックエンドの 403 が決めます。ここでの判定は UI 用です。
 */
export const canUseMosPosMenuMasterPage = (
  me?: ManagerAccountMe,
  enterpriseIds: number[] = MOS_ENTERPRISE_IDS
): boolean =>
  me !== undefined &&
  me.role_type === '管理者' &&
  enterpriseIds.includes(me.enterprise_id);

/**
 * 権限が無ければ /404 へ飛ばします。
 * me が undefined の間は何もしません。アカウント読み込み中に誤ってリダイレクト
 * してしまうのを防ぐためです。
 */
export const redirect404MosPosMenuMasterPage = (
  history: RouterChildContext['router']['history'],
  me?: ManagerAccountMe
): void => {
  if (me === undefined) return;
  if (canUseMosPosMenuMasterPage(me)) return;
  history.push('/404');
};

/**
 * 拠点一覧に「タブレット画面を開く」(代理ログイン) を表示するかどうかを判定します。
 *
 * 企業IDの許可リスト (PROXY_LOGIN_ENTERPRISE_IDS) に含まれる企業の管理者だけを通します。
 * 代理ログインは店舗のタブレットの全権限を渡すため、管理者だけに限ります。
 * 最終的な可否はバックエンドの代理ログイン発行APIが決めます。ここでの判定は UI 用です。
 */
export const canUseStoreProxyLogin = (
  me?: ManagerAccountMe,
  enterpriseIds: number[] = PROXY_LOGIN_ENTERPRISE_IDS
): boolean =>
  me !== undefined &&
  me.role_type === '管理者' &&
  enterpriseIds.includes(me.enterprise_id);
